import * as React from "react";
import { useQueryClient } from "react-query";
import * as S from "@styled";
import Breadcrumbs from "components/shared/Breadcrumbs";
import {
    INTEGRATION_CATALOG_BASE_PATH,
    TEMPLATES_API_BASE_PATH,
} from "AppRoutes";
import { Breadcrumb } from "@frends/ui-components/dist/types/breadcrumbs/Breadcrumbs";
import { useParams, useNavigate } from "react-router-dom";
import useQuery from "hooks/useQuery";
import useIntersectionObserver from "hooks/useIntersectionObserver";
import { TemplateDetail } from "model/Template";
import Button from "components/shared/Button";
import Table from "components/shared/Table";
import TitleWithArrowLink from "components/shared/TitleWithArrowLink";
import ContainerWithLoadingSpinner from "components/shared/ContainerWithLoadingSpinner";
import { TemplateChangeLogVersion } from "model/TemplateChangeLog";
import { PagedResponse } from "model/PagedResponse";
import Time from "shared/Time";

const TemplateChangeLog: React.FC = () => {
    const navigate = useNavigate();
    const { templateId } = useParams();

    const templateResponse = useQuery<TemplateDetail>(
        `${TEMPLATES_API_BASE_PATH}/${templateId}`,
        undefined,
        undefined,
        "Failed to fetch template details.",
    );
    const template = templateResponse.data;

    const templateDetailsRoute = `${INTEGRATION_CATALOG_BASE_PATH}/${templateId}`;
    const breadcrumbs: Breadcrumb[] = [
        {
            label: "Integration catalog",
            href: INTEGRATION_CATALOG_BASE_PATH,
        },
        {
            label: "Template details",
            href: templateDetailsRoute,
        },
        {
            label: "Change log",
            href: `${INTEGRATION_CATALOG_BASE_PATH}/${templateId}/changelog`,
        },
    ];

    return (
        <S.TemplateDetailsPageContainer>
            <ContainerWithLoadingSpinner
                loading={
                    templateResponse.isLoading || templateResponse.isFetching
                }
            >
                <Breadcrumbs breadcrumbs={breadcrumbs} onClick={navigate} />
                <S.ContentContainer>
                    <S.TemplateDetailsContentContainer>
                        {templateResponse.isLoading && (
                            <TitleWithArrowLink
                                href={templateDetailsRoute}
                                title="Loading template details..."
                            />
                        )}

                        {!templateResponse.isLoading && template && (
                            <>
                                <TitleWithArrowLink
                                    title={template.name}
                                    href={templateDetailsRoute}
                                />
                                <ChangeLogTable
                                    templateUniqueIdentifier={
                                        template.uniqueIdentifier
                                    }
                                />
                                <S.TemplateChangeLogActionsContainer>
                                    <Button
                                        type="link"
                                        href={templateDetailsRoute}
                                    >
                                        Back to template details
                                    </Button>
                                    <Button
                                        href={`mailto:${template.modifier}`}
                                        variant="bordered"
                                    >
                                        Contact integration developer
                                    </Button>
                                </S.TemplateChangeLogActionsContainer>
                            </>
                        )}

                        {!templateResponse.isLoading && !template && (
                            <TitleWithArrowLink
                                href={INTEGRATION_CATALOG_BASE_PATH}
                                title="Template could not be loaded"
                            />
                        )}
                    </S.TemplateDetailsContentContainer>
                </S.ContentContainer>
            </ContainerWithLoadingSpinner>
        </S.TemplateDetailsPageContainer>
    );
};

const ChangeLogTable: React.FC<ChangeLogTableProps> = (props) => {
    const changeLogAPIPath = `${TEMPLATES_API_BASE_PATH}/changelog`;

    const queryClient = useQueryClient();
    const [pageNumber, setPageNumber] = React.useState<number>(1);
    const [templateVersions, setTemplateVersions] = React.useState<
        TemplateChangeLogVersion[] | undefined
    >();

    const templateChangeLogGET = useQuery<
        PagedResponse<TemplateChangeLogVersion>
    >(
        changeLogAPIPath,
        {
            templateGuid: props.templateUniqueIdentifier,
            pageNumber: pageNumber,
            // API endpoint for fetching template versions doesn't have sorting ATM.
            // So let's pull relatively large amount of (50) items initially and do the sorting by modified date in FE for now.
            // The component will also load more items on scroll if needed.
            pageSize: 50,
        },
        undefined,
        "Failed to fetch template change log.",
    );

    const hasMore = !!templateChangeLogGET.data?.paging?.nextPage;

    const loadMoreTriggerDivRef = React.useRef<HTMLDivElement | null>(null);

    useIntersectionObserver({
        blockIntersectAction: templateChangeLogGET.isLoading,
        intersectionObserverDependencies: [
            templateChangeLogGET.isLoading,
            hasMore,
            queryClient,
        ],
        intersectionRef: loadMoreTriggerDivRef,
        onIntersect: () => {
            if (hasMore) {
                setPageNumber((prevPage) => prevPage + 1);
                queryClient.invalidateQueries(changeLogAPIPath);
            }
        },
    });

    React.useEffect(() => {
        if (templateChangeLogGET.data) {
            const nextTemplateVersions = [
                ...(templateVersions ? templateVersions : []),
                ...templateChangeLogGET.data.data,
            ];

            nextTemplateVersions.sort(
                (t1, t2) =>
                    Time.fromUtcToLocal(t2.modifiedUtc).getTime() -
                    Time.fromUtcToLocal(t1.modifiedUtc).getTime(),
            );

            setTemplateVersions(nextTemplateVersions);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateChangeLogGET.data]);

    if (
        (!templateVersions || templateVersions.length === 0) &&
        !templateChangeLogGET.isLoading
    ) {
        return <div>Change log could not be loaded</div>;
    }

    return (
        <ContainerWithLoadingSpinner loading={templateChangeLogGET.isLoading}>
            {templateVersions && templateVersions?.length > 0 && (
                <Table>
                    <S.TemplateChangeLogTable>
                        <thead>
                            <tr>
                                <th>Version</th>
                                <th>Time</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            {templateVersions.map((version, key) => {
                                return (
                                    <tr key={key}>
                                        <td>{version.version}</td>
                                        <td>
                                            {Time.toDetailedLocalTime(
                                                version.modifiedUtc,
                                            )}
                                        </td>
                                        <td>{version.description}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </S.TemplateChangeLogTable>
                </Table>
            )}

            {hasMore && (
                <div ref={loadMoreTriggerDivRef} style={{ height: "1px" }} />
            )}
        </ContainerWithLoadingSpinner>
    );
};

interface ChangeLogTableProps {
    templateUniqueIdentifier: string;
}

export default TemplateChangeLog;
